import { Card } from "@/components/ui/Card";
import { ONLINE_BOOKING_MODE_LABELS } from "@/lib/onlineBooking";
import { OnlineBookingFormHint } from "@/pages/stammdaten/online-booking/OnlineBookingFormHint";
import type { OnlineBookingSettings } from "@/pages/stammdaten/online-booking/useOnlineBookingSettings";

type Props = Pick<
  OnlineBookingSettings,
  | "publicBookingEnabled"
  | "setPublicBookingEnabled"
  | "onlineBookingMode"
  | "setOnlineBookingMode"
  | "onlineBookingAvailabilityCheck"
  | "setOnlineBookingAvailabilityCheck"
  | "touristIntegrationEnabled"
  | "autoSendConfirmationEmail"
  | "setAutoSendConfirmationEmail"
> & { canWrite: boolean };

export function OnlineBookingBetriebPanel({
  canWrite,
  publicBookingEnabled,
  setPublicBookingEnabled,
  onlineBookingMode,
  setOnlineBookingMode,
  onlineBookingAvailabilityCheck,
  setOnlineBookingAvailabilityCheck,
  touristIntegrationEnabled,
  autoSendConfirmationEmail,
  setAutoSendConfirmationEmail,
}: Props) {
  return (
    <div className="space-y-6">
      <Card
        title="Website & Buchungsmodus"
        description="Schaltet die öffentliche Buchungswebsite ein und legt fest, wie eingehende Buchungen behandelt werden."
        bodyClassName="space-y-4"
      >
        <fieldset className="space-y-3" disabled={!canWrite}>
          <label className="flex min-w-0 items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1 shrink-0"
              checked={publicBookingEnabled}
              onChange={(e) => setPublicBookingEnabled(e.target.checked)}
            />
            <span>
              Öffentliche Website-Buchung aktiv
              <span className="mt-0.5 block text-xs text-label-secondary">
                Wenn deaktiviert, ist die Buchungswebsite für Gäste nicht erreichbar. Channel-Partner
                über die Integrations-API können weiterhin buchen, sofern API-Keys hinterlegt sind.
              </span>
            </span>
          </label>
          <fieldset className="min-w-0 space-y-2 rounded-lg border border-separator p-3">
            <legend className="px-1 text-sm font-medium text-label">
              Eingehende Online-Buchungen
            </legend>
            <OnlineBookingFormHint>
              <strong>Anfragen</strong> eignen sich zum Testen und wenn die Rezeption jede Buchung
              prüfen soll. <strong>Sofort buchen</strong> reserviert freie Kapazität automatisch.
            </OnlineBookingFormHint>
            <label className="flex min-w-0 items-start gap-2 text-sm">
              <input
                type="radio"
                name="online-booking-mode"
                className="mt-1 shrink-0"
                checked={onlineBookingMode === "inquiry"}
                onChange={() => setOnlineBookingMode("inquiry")}
              />
              <span>
                <strong>{ONLINE_BOOKING_MODE_LABELS.inquiry}</strong> – Status „Angefragt“,
                Rezeption bestätigt manuell
              </span>
            </label>
            <label className="flex min-w-0 items-start gap-2 text-sm">
              <input
                type="radio"
                name="online-booking-mode"
                className="mt-1 shrink-0"
                checked={onlineBookingMode === "reservation"}
                onChange={() => {
                  setOnlineBookingMode("reservation");
                  setOnlineBookingAvailabilityCheck(true);
                }}
              />
              <span>
                <strong>{ONLINE_BOOKING_MODE_LABELS.reservation}</strong> – sofort bestätigt
              </span>
            </label>
          </fieldset>
          <label className="flex min-w-0 items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1 shrink-0"
              checked={onlineBookingAvailabilityCheck}
              disabled={onlineBookingMode === "reservation" || touristIntegrationEnabled}
              onChange={(e) => setOnlineBookingAvailabilityCheck(e.target.checked)}
            />
            <span>
              Verfügbarkeitsprüfung in der Buchungsstrecke
              <span className="mt-0.5 block text-xs text-label-secondary">
                {touristIntegrationEnabled
                  ? "Bei aktiver Tourist-Anbindung ist die Verfügbarkeitsprüfung deaktiviert. Ausgebuchte Nächte konfigurieren Sie unter Schnittstellen → Tourist-Anbindung."
                  : onlineBookingMode === "reservation"
                    ? "Bei sofortiger Buchung ist die Verfügbarkeitsprüfung immer aktiv."
                    : "Ohne Prüfung können Gäste jede Platzkategorie anfragen; die Kapazität wird erst in der Rezeption geprüft."}
              </span>
            </span>
          </label>
        </fieldset>
      </Card>

      <Card
        title="Benachrichtigungen"
        description="Automatische E-Mails an Gäste nach einer Buchung oder Anfrage."
        bodyClassName="space-y-4"
      >
        <fieldset className="space-y-3" disabled={!canWrite}>
          <label className="flex min-w-0 items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1 shrink-0"
              checked={autoSendConfirmationEmail}
              onChange={(e) => setAutoSendConfirmationEmail(e.target.checked)}
            />
            <span>Buchungsbestätigung per E-Mail automatisch senden</span>
          </label>
        </fieldset>
      </Card>
    </div>
  );
}
